/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef } from "react";
import { Camera, X, Upload } from "lucide-react";
import PhotoPreview from "./PhotoPreview";

/**
 * Two-step inline photo upload button (with drag & drop):
 * 1. Click OR drop → open file picker / accept dropped image
 * 2. On file pick → show preview + [Upload] [Cancel]
 * 3. On Upload → call onUpload(file); auto-clear on success
 *
 * Use for row-level single-file uploads where the user should
 * confirm what they're sending before it hits the server.
 */
export default function PhotoUploadButton({
  onUpload,
  disabled = false,
  label,
  testid,
}) {
  const [pending, setPending] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const pick = () => fileRef.current?.click();

  const onPicked = (e) => {
    const f = e.target.files?.[0];
    if (f) setPending(f);
    e.target.value = "";
  };

  const cancel = () => setPending(null);

  const send = async () => {
    if (!pending) return;
    setUploading(true);
    try {
      await onUpload(pending);
      setPending(null);
    } finally {
      setUploading(false);
    }
  };

  const onDragOver = (e) => {
    if (disabled) return;
    if (Array.from(e.dataTransfer?.types || []).includes("Files")) {
      e.preventDefault();
      setDragOver(true);
    }
  };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    if (disabled) return;
    e.preventDefault();
    setDragOver(false);
    const f = Array.from(e.dataTransfer?.files || []).find((x) => x.type.startsWith("image/")) || e.dataTransfer?.files?.[0];
    if (f) setPending(f);
  };

  return (
    <div className="inline-flex flex-col gap-2" data-testid={testid}>
      <input
        type="file"
        accept="image/*"
        ref={fileRef}
        onChange={onPicked}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      {!pending && (
        <button
          type="button"
          onClick={pick}
          onDragOver={onDragOver}
          onDragEnter={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          disabled={disabled}
          className={`flex items-center gap-1 border px-2 py-1 text-xs uppercase tracking-widest transition-colors disabled:opacity-50 ${
            dragOver
              ? "border-readyGreen bg-readyGreen/10 text-readyGreen"
              : "border-cmdBorder hover:border-white"
          }`}
          data-testid={`${testid}-pick`}
          title="Click to pick or drag & drop an image here"
        >
          <Camera className="w-3 h-3" /> {dragOver ? "Drop to attach" : (label || "Photo")}
        </button>
      )}
      {pending && (
        <div className="border border-warnAmber bg-warnAmber/5 p-2 flex items-start gap-2" data-testid={`${testid}-review`}>
          <PhotoPreview files={[pending]} testidPrefix={`${testid}-thumb`} size={72} />
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={send}
              disabled={uploading}
              className="flex items-center gap-1 border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white px-2 py-1 text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50"
              data-testid={`${testid}-confirm`}
            >
              <Upload className="w-3 h-3" /> {uploading ? "Sending…" : "Upload"}
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={uploading}
              className="flex items-center gap-1 border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-[10px] uppercase tracking-widest transition-colors"
              data-testid={`${testid}-cancel`}
            >
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
